import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { InputField } from '../../../../../../components/atoms/input-field';

import { useUpdateDestinationKioskDialogValidationSchema } from './schema/update-destination-kiosk-dialog-validation-schema';

import { Dialog, DialogActions as DialogActionsInterface } from '@/components/molecules/dialog';
import { useUpdateDestination } from '@/features/destinations/hooks/mutation/use-update-destination';
import { useOrganizationKiosks } from '@/features/shared/hooks/query/use-organization-kiosks';
import { getLabel } from '@/utils/get-label';
import { getMessage } from '@/utils/get-message';

interface Props extends DialogActionsInterface {
  id: number;
  allowedKioskIds: number[];
  deviceIds: number[];
  hosts: { id: number; firstName: string; lastName: string; email: string }[];
  addHostStepEnabled: boolean;
  hostObligatory: boolean;
  hostConfirmationObligatory: boolean;
  hostConfirmationInEmail: boolean;
  deviceConfirmation: boolean;
  defaultHostId: number | null;
  kioskVisitHours: number;
}

interface FormValues {
  allowedKioskIds: number[];
  defaultHostId: number | null;
  addHostStepEnabled: boolean;
  hostObligatory: boolean;
  hostConfirmationObligatory: boolean;
  hostConfirmationInEmail: boolean;
  deviceConfirmation: boolean;
  kioskVisitHours: number;
}

export const UpdateDestinationKioskDialog = ({
  id,
  allowedKioskIds,
  deviceIds,
  hosts,
  addHostStepEnabled,
  hostObligatory,
  hostConfirmationObligatory,
  hostConfirmationInEmail,
  deviceConfirmation,
  defaultHostId,
  kioskVisitHours,
  open,
  onCancel,
}: Props) => {
  const { t } = useTranslation(['createDestination', 'destination', 'common']);
  const { data: organizationKiosks } = useOrganizationKiosks();

  const validationSchema = useUpdateDestinationKioskDialogValidationSchema();

  const defaultValues: FormValues = {
    allowedKioskIds,
    defaultHostId,
    addHostStepEnabled,
    hostObligatory,
    hostConfirmationObligatory,
    hostConfirmationInEmail,
    deviceConfirmation,
    kioskVisitHours: kioskVisitHours ?? 1,
  };

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { isValid, isDirty },
  } = useForm<FormValues>({
    defaultValues,
    resolver: zodResolver(validationSchema),
    mode: 'onBlur',
  });

  const [
    selectedKioskIds,
    selectedDefaultHostId,
    isAddHostStepEnabled,
    isHostObligatory,
    isHostConfirmationObligatory,
    isHostConfirmationInEmail,
    isDeviceConfirmationEnabled,
  ] = useWatch({
    control,
    name: [
      'allowedKioskIds',
      'defaultHostId',
      'addHostStepEnabled',
      'hostObligatory',
      'hostConfirmationObligatory',
      'hostConfirmationInEmail',
      'deviceConfirmation',
    ],
  });
  const isKioskEnabled = (selectedKioskIds?.length || 0) > 0;
  const normalizedDefaultHostId = selectedDefaultHostId ?? null;
  const isDefaultHostAssigned = normalizedDefaultHostId !== null;
  const hasHosts = hosts.length > 0;
  const canUseDefaultHost = hasHosts && isKioskEnabled;
  const canUseHostStep = isKioskEnabled && !isDefaultHostAssigned;
  const canUseHostObligatory = isAddHostStepEnabled;
  const canUseHostConfirmation = isDefaultHostAssigned
    ? isKioskEnabled
    : canUseHostStep && isHostObligatory;
  const hasAssignedDevices = (deviceIds?.length || 0) > 0;
  const canUseDeviceConfirmation = isKioskEnabled && hasAssignedDevices;

  const { mutate, isPending } = useUpdateDestination(onCancel);

  const onSubmit = (data: FormValues) => {
    // Handle defaultHostId logic:
    // - If original was null and unchanged, send undefined
    // - Only send null if there was an ID and changed to null
    let processedDefaultHostId: number | null | undefined;
    if (defaultHostId === null && data.defaultHostId === null) {
      processedDefaultHostId = undefined;
    } else {
      processedDefaultHostId = data.defaultHostId;
    }

    mutate({
      payload: {
        ...data,
        addHostStepEnabled: isDefaultHostAssigned ? false : data.addHostStepEnabled,
        hostObligatory: isDefaultHostAssigned ? false : data.hostObligatory,
        hostConfirmationObligatory: data.hostConfirmationObligatory,
        hostConfirmationInEmail: data.hostConfirmationInEmail,
        deviceConfirmation: data.deviceConfirmation,
        defaultHostId: processedDefaultHostId,
        kioskVisitHours: data.allowedKioskIds.length > 0 ? data.kioskVisitHours : undefined,
      },
      destinationId: id,
    });
  };

  useEffect(() => {
    if (!open) {
      return;
    }

    reset(defaultValues);
  }, [
    open,
    reset,
    allowedKioskIds,
    addHostStepEnabled,
    hostObligatory,
    hostConfirmationObligatory,
    hostConfirmationInEmail,
    deviceConfirmation,
    defaultHostId,
    hosts,
    kioskVisitHours,
  ]);

  useEffect(() => {
    const setValueOptions = {
      shouldDirty: true,
      shouldValidate: true,
    } as const;

    let nextDefaultHostId = normalizedDefaultHostId;
    let nextAddHostStepEnabled = isAddHostStepEnabled;
    let nextHostObligatory = isHostObligatory;
    let nextHostConfirmationObligatory = isHostConfirmationObligatory;
    let nextHostConfirmationInEmail = isHostConfirmationInEmail;
    let nextDeviceConfirmation = isDeviceConfirmationEnabled;

    if (!hasHosts) {
      nextDefaultHostId = null;
    }

    if (!isKioskEnabled) {
      nextDefaultHostId = null;
    }

    const hasDefaultHost = nextDefaultHostId !== null;
    const shouldDisableHostFlow = !isKioskEnabled || hasDefaultHost;

    if (shouldDisableHostFlow) {
      nextAddHostStepEnabled = false;
    }

    if (!nextAddHostStepEnabled || shouldDisableHostFlow) {
      nextHostObligatory = false;
    }

    if (!nextHostObligatory && !hasDefaultHost) {
      nextHostConfirmationObligatory = false;
    }

    if (!nextHostConfirmationObligatory) {
      nextHostConfirmationInEmail = false;
    }

    if (!canUseDeviceConfirmation) {
      nextDeviceConfirmation = false;
    }

    if (nextDefaultHostId !== normalizedDefaultHostId) {
      setValue('defaultHostId', nextDefaultHostId, setValueOptions);
    }

    if (nextAddHostStepEnabled !== isAddHostStepEnabled) {
      setValue('addHostStepEnabled', nextAddHostStepEnabled, setValueOptions);
    }

    if (nextHostObligatory !== isHostObligatory) {
      setValue('hostObligatory', nextHostObligatory, setValueOptions);
    }

    if (nextHostConfirmationObligatory !== isHostConfirmationObligatory) {
      setValue('hostConfirmationObligatory', nextHostConfirmationObligatory, setValueOptions);
    }

    if (nextHostConfirmationInEmail !== isHostConfirmationInEmail) {
      setValue('hostConfirmationInEmail', nextHostConfirmationInEmail, setValueOptions);
    }

    if (nextDeviceConfirmation !== isDeviceConfirmationEnabled) {
      setValue('deviceConfirmation', nextDeviceConfirmation, setValueOptions);
    }
  }, [
    isAddHostStepEnabled,
    isKioskEnabled,
    isHostObligatory,
    isHostConfirmationObligatory,
    isHostConfirmationInEmail,
    isDeviceConfirmationEnabled,
    canUseDeviceConfirmation,
    hasHosts,
    isDefaultHostAssigned,
    normalizedDefaultHostId,
    setValue,
  ]);

  return (
    <Dialog
      title={t('destination:updateKioskDialogTitle')}
      onCancel={onCancel}
      open={open}
      confirmButtonText={t('common:button.edit')}
      isConfirmButtonDisabled={!isValid || !isDirty}
      isConfirmButtonSubmitting={isPending}
      onConfirm={handleSubmit(onSubmit)}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Controller
            control={control}
            name="allowedKioskIds"
            render={({ field }) => {
              const selectedIds = Array.isArray(field.value)
                ? field.value.map(item => Number(item))
                : [];

              return (
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                    {t('destination:kiosk.allowedKiosks')}
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gap: 0.5,
                      borderWidth: 1,
                      borderStyle: 'solid',
                      borderColor: 'neutral.300',
                      borderRadius: 1,
                      p: 1,
                    }}>
                    {(organizationKiosks || []).map(({ id, name }) => (
                      <FormControlLabel
                        key={id}
                        sx={{ m: 0, width: '100%', justifyContent: 'space-between' }}
                        labelPlacement="start"
                        control={
                          <Checkbox
                            checked={selectedIds.includes(id)}
                            onChange={(_, checked) => {
                              const nextSelectedIds = checked
                                ? Array.from(new Set([...selectedIds, id]))
                                : selectedIds.filter(kioskId => kioskId !== id);

                              field.onChange(nextSelectedIds);
                            }}
                            onBlur={field.onBlur}
                            name={field.name}
                          />
                        }
                        label={name}
                      />
                    ))}
                  </Box>
                </Box>
              );
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <Tooltip
            title={getMessage([
              {
                check: !isKioskEnabled,
                message: t('destination:kiosk.addHostStepEnabledDisabled'),
              },
              {
                check: isKioskEnabled && !hasHosts,
                message: t('destination:kiosk.defaultHostDisabledNoHosts'),
              },
            ])}
            disableHoverListener={canUseDefaultHost}
            disableFocusListener={canUseDefaultHost}>
            <span>
              <Controller
                control={control}
                name="defaultHostId"
                render={({ field }) => {
                  return (
                    <InputField
                      fullWidth
                      select
                      value={field.value ?? ''}
                      onChange={event => {
                        const value = event.target.value;
                        const nextDefaultHostId = value === '' ? null : Number(value);

                        field.onChange(nextDefaultHostId);
                      }}
                      onBlur={field.onBlur}
                      name={field.name}
                      disabled={!canUseDefaultHost}
                      label={t('destination:kiosk.defaultHost')}>
                      <MenuItem value="">{t('destination:kiosk.defaultHostNotSet')}</MenuItem>
                      {hosts.map(host => (
                        <MenuItem key={host.id} value={host.id}>
                          {getLabel({
                            values: [host.firstName, host.lastName],
                            fallback: host.email,
                          })}
                        </MenuItem>
                      ))}
                    </InputField>
                  );
                }}
              />
            </span>
          </Tooltip>
        </Grid>
        <Grid item xs={12}>
          <Tooltip
            title={getMessage([
              {
                check: !canUseHostStep && !isKioskEnabled,
                message: t('destination:kiosk.addHostStepEnabledDisabled'),
              },
              {
                check: !canUseHostStep && isKioskEnabled,
                message: t('destination:kiosk.addHostStepEnabledDisabledByDefaultHost'),
              },
            ])}
            disableHoverListener={canUseHostStep}
            disableFocusListener={canUseHostStep}>
            <span>
              <Controller
                control={control}
                name="addHostStepEnabled"
                render={({ field }) => (
                  <FormControlLabel
                    sx={{ m: 0, width: '100%', justifyContent: 'space-between' }}
                    labelPlacement="start"
                    control={
                      <Checkbox
                        checked={!!field.value}
                        onChange={(_, checked) => field.onChange(checked)}
                        onBlur={field.onBlur}
                        name={field.name}
                        disabled={!canUseHostStep}
                      />
                    }
                    label={t('destination:kiosk.addHostStepEnabled')}
                  />
                )}
              />
            </span>
          </Tooltip>
        </Grid>
        <Grid item xs={12}>
          <Tooltip
            title={getMessage([
              {
                check: !canUseHostObligatory && isDefaultHostAssigned,
                message: t('destination:kiosk.hostObligatoryDisabledByDefaultHost'),
              },
              {
                check: !canUseHostObligatory && !isDefaultHostAssigned,
                message: t('destination:kiosk.hostObligatoryDisabled'),
              },
            ])}
            disableHoverListener={canUseHostObligatory}
            disableFocusListener={canUseHostObligatory}>
            <span>
              <Controller
                control={control}
                name="hostObligatory"
                render={({ field }) => (
                  <FormControlLabel
                    sx={{ m: 0, width: '100%', justifyContent: 'space-between' }}
                    labelPlacement="start"
                    control={
                      <Checkbox
                        checked={!!field.value}
                        onChange={(_, checked) => field.onChange(checked)}
                        onBlur={field.onBlur}
                        name={field.name}
                        disabled={!canUseHostObligatory}
                      />
                    }
                    label={t('destination:kiosk.hostObligatory')}
                  />
                )}
              />
            </span>
          </Tooltip>
        </Grid>
        <Grid item xs={12}>
          <Tooltip
            title={getMessage([
              {
                check: !canUseHostConfirmation && !isKioskEnabled,
                message: t('destination:kiosk.addHostStepEnabledDisabled'),
              },
              {
                check: !canUseHostConfirmation && isKioskEnabled,
                message: t('destination:kiosk.hostConfirmationObligatoryDisabled'),
              },
            ])}
            disableHoverListener={canUseHostConfirmation}
            disableFocusListener={canUseHostConfirmation}>
            <span>
              <Controller
                control={control}
                name="hostConfirmationObligatory"
                render={({ field }) => (
                  <FormControlLabel
                    sx={{ m: 0, width: '100%', justifyContent: 'space-between' }}
                    labelPlacement="start"
                    control={
                      <Checkbox
                        checked={!!field.value}
                        onChange={(_, checked) => field.onChange(checked)}
                        onBlur={field.onBlur}
                        name={field.name}
                        disabled={!canUseHostConfirmation}
                      />
                    }
                    label={t('destination:kiosk.hostConfirmationObligatory')}
                  />
                )}
              />
            </span>
          </Tooltip>
        </Grid>
        <Grid item xs={12}>
          <Tooltip
            title={getMessage([
              {
                check: !canUseHostConfirmation && !isKioskEnabled,
                message: t('destination:kiosk.addHostStepEnabledDisabled'),
              },
              {
                check: !canUseHostConfirmation && isKioskEnabled,
                message: t('destination:kiosk.hostConfirmationObligatoryDisabled'),
              },
              {
                check: canUseHostConfirmation && !isHostConfirmationObligatory,
                message: t('destination:kiosk.hostConfirmationInEmailDisabled'),
              },
            ])}
            disableHoverListener={canUseHostConfirmation && isHostConfirmationObligatory}
            disableFocusListener={canUseHostConfirmation && isHostConfirmationObligatory}>
            <span>
              <Controller
                control={control}
                name="hostConfirmationInEmail"
                render={({ field }) => (
                  <FormControlLabel
                    sx={{ m: 0, width: '100%', justifyContent: 'space-between' }}
                    labelPlacement="start"
                    control={
                      <Checkbox
                        checked={!!field.value}
                        onChange={(_, checked) => field.onChange(checked)}
                        onBlur={field.onBlur}
                        name={field.name}
                        disabled={!canUseHostConfirmation || !isHostConfirmationObligatory}
                      />
                    }
                    label={t('destination:kiosk.hostConfirmationInEmail')}
                  />
                )}
              />
            </span>
          </Tooltip>
        </Grid>
        <Grid item xs={12}>
          <Tooltip
            title={getMessage([
              {
                check: !isKioskEnabled,
                message: t('destination:kiosk.deviceConfirmationDisabledNoKiosksSelected'),
              },
              {
                check: isKioskEnabled && !hasAssignedDevices,
                message: t('destination:kiosk.deviceConfirmationDisabledNoDevices'),
              },
            ])}
            disableHoverListener={canUseDeviceConfirmation}
            disableFocusListener={canUseDeviceConfirmation}>
            <span>
              <Controller
                control={control}
                name="deviceConfirmation"
                render={({ field }) => (
                  <FormControlLabel
                    sx={{ m: 0, width: '100%', justifyContent: 'space-between' }}
                    labelPlacement="start"
                    control={
                      <Checkbox
                        checked={!!field.value}
                        onChange={(_, checked) => field.onChange(checked)}
                        onBlur={field.onBlur}
                        name={field.name}
                        disabled={!canUseDeviceConfirmation}
                      />
                    }
                    label={t('destination:kiosk.deviceConfirmation')}
                  />
                )}
              />
            </span>
          </Tooltip>
        </Grid>
        <Grid item xs={12}>
          <Tooltip
            title={!isKioskEnabled ? t('destination:kiosk.kioskVisitHoursDisabled') : ''}
            disableHoverListener={isKioskEnabled}
            disableFocusListener={isKioskEnabled}>
            <span>
              <Controller
                control={control}
                name="kioskVisitHours"
                render={({ field, fieldState: { error } }) => (
                  <InputField
                    {...field}
                    fullWidth
                    label={t('destination:kiosk.kioskVisitHours')}
                    required
                    type="number"
                    error={!!error?.message}
                    helperText={error?.message}
                    disabled={!isKioskEnabled}
                  />
                )}
              />
            </span>
          </Tooltip>
        </Grid>
      </Grid>
    </Dialog>
  );
};
